import { useMemo } from 'react';
import { statusMatchesReference } from './isEveryPropertyDefined.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import type { ReroutingTransition } from './ReroutingTransitionValid.types.tsx';
import type { DiagramSharedTransition, DiagramStatus } from './toPortInfo.tsx';
import type { DragDropState } from './useCanvasDragDropEvents.tsx';

export interface Options {
	hoveredItem: ItemReference | undefined;
	statuses: DiagramStatus[];
	dragDropState: DragDropState;
	isProposingTransition: boolean;
	reroutingTransition: ReroutingTransition;
}

/** Provides a memoized list of Status IDs that should show
 *  their associated Ports.
 */
export const useStatusesThatShouldShowPorts = ({
	hoveredItem,
	statuses,
	dragDropState,
	isProposingTransition,
	reroutingTransition,
}: Options): string[] => {
	const statusesThatShouldShowPorts = useMemo(() => {
		if (dragDropState.isDragging && dragDropState.dragSubject.type === 'status') {
			return [];
		}

		const isReroutingDirectTransition =
			reroutingTransition.stage !== 'none' && reroutingTransition.transition.type === 'direct';

		if (isProposingTransition || isReroutingDirectTransition) {
			return statuses.map(({ id }) => id);
		}

		if (reroutingTransition.stage !== 'none') {
			if (reroutingTransition.side === 'target') {
				return statuses.map(({ id }) => id);
			}

			// ignore ports from statuses that are already in the source list from showing,
			// except the one we're moving from already
			const reroutingSharedTransition =
				// oxlint-disable-next-line typescript/consistent-type-assertions
				reroutingTransition.transition as DiagramSharedTransition;

			const otherSourceStatusIds = reroutingSharedTransition.links
				.filter((link) => link.source !== reroutingTransition.originalStatusId)
				.map(({ source }) => source);

			return statuses.filter(({ id }) => !otherSourceStatusIds.includes(id)).map(({ id }) => id);
		}

		return statuses.filter((n) => statusMatchesReference(n, hoveredItem)).map(({ id }) => id);
	}, [dragDropState, reroutingTransition, isProposingTransition, statuses, hoveredItem]);

	return statusesThatShouldShowPorts;
};

export default useStatusesThatShouldShowPorts;
